import React, { useEffect, useMemo, useState } from 'react';
import { useAppShell } from '../contexts/AppShellContext';
import { listJobs } from '../services/jobService';
import { PageHeader } from '../components/PageHeader';
import { SearchInput } from '../components/SearchInput';
import { SortableTh } from '../components/SortableTh';
import { useSort, sortRows } from '../utils/useSort';
import { pillStyle, type PillColor } from '../utils/pillStyle';
import { windowBounds, inWindow, windowCounts, type ScopeValue } from '../utils/timeWindow';

interface JobRow {
  id: string;
  client: string;
  type: string;
  job_status: string | null;
  date: string; // YYYY-MM-DD (created_at)
}

// job_status → shared sp-* badge class. Mirrors ReceivingLog's STATUS_MAP so a
// job shows the same pill everywhere. P/CA/I use Received/Sample/Mailing/Complete/
// Aborted; Regulatory (R) uses CS-X/PSD/TSR/X (per data-structure.html JobStatus).
const STATUS_CLS: Record<string, string> = {
  Received: 'sp-r',
  Sample:   'sp-v',
  Mailing:  'sp-m',
  Complete: 'sp-r',
  Aborted:  'sp-e',
  'CS-X':   'sp-w',
  PSD:      'sp-r',
  TSR:      'sp-o',
  X:        'sp-e',
};

// Canonical job_status order — filter pills list only the values present in the data.
const STATUS_ORDER = ['Received', 'Sample', 'Mailing', 'Complete', 'Aborted'];

// Filter-pill colours per job_status — matched to the sp-* palette (same approach
// as ReceivingLog's PILL_PALETTE).
const STATUS_PILL: Record<string, PillColor> = {
  Received: { bg: '#E9F6F6', fg: '#0F6E56' },
  Sample:   { bg: '#DBEAFE', fg: '#1d4ed8' },
  Mailing:  { bg: '#EDE9FE', fg: '#6d28d9' },
  Complete: { bg: '#E9F6F6', fg: '#0F6E56' },
  Aborted:  { bg: '#FEF2F2', fg: '#b91c1c' },
  'CS-X':   { bg: '#FEF2F2', fg: '#b91c1c', border: '#FECACA' },
  PSD:      { bg: '#E9F6F6', fg: '#0F6E56' },
  TSR:      { bg: '#FFFBEB', fg: '#92400E', border: '#FCD34D' },
  X:        { bg: '#FEF2F2', fg: '#b91c1c' },
};

const COLUMNS = ['Job ID', 'Client', 'Type', 'Status', 'Date'];

function cellValue(j: JobRow, column: string): string {
  switch (column) {
    case 'Job ID':  return j.id;
    case 'Client':  return j.client;
    case 'Type':    return j.type;
    case 'Status':  return j.job_status ?? '';
    case 'Date':    return j.date;
    default:        return '';
  }
}

function StatusBadge({ status }: { status: string | null }) {
  if (!status) return <span className="text-xs text-muted">—</span>;
  const cls = STATUS_CLS[status] ?? 'sp-pending';
  return <span className={`sp text-xs whitespace-nowrap ${cls}`}>{status}</span>;
}

export function AllJobsPage() {
  const { setTopbar } = useAppShell();
  const [filter, setFilter]   = useState('All');
  const [search, setSearch]   = useState('');
  // Time scope is rendered for parity but wired to created_at as an interim
  // variable — the full multi-variable scoping (created / updated / mailing /
  // ordered date) lands with the jobs-DB integration.
  const [scope, setScope]     = useState<ScopeValue>({ window: 'month' });
  const { sort, toggle }      = useSort();
  const [jobs, setJobs]       = useState<JobRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState<string | null>(null);

  useEffect(() => { setTopbar('Jobs', 'All jobs in the system'); }, [setTopbar]);

  useEffect(() => {
    listJobs()
      .then(r => setJobs(r.jobs.map(j => ({
        id:     j.job_number,
        client: j.client_name ?? '',
        type:   j.description,
        job_status: j.job_status,
        date:   j.created_at.split('T')[0],
      }))))
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  // intake_date is the field windowCounts/inWindow read; map created_at onto it.
  const dated = useMemo(() => jobs.map(j => ({ ...j, intake_date: j.date })), [jobs]);
  const winCounts = useMemo(() => windowCounts(dated), [dated]);

  const windowedJobs = useMemo(() => {
    const bounds = windowBounds(scope);
    return jobs.filter(j => inWindow(j.date, bounds));
  }, [jobs, scope]);

  const total = windowedJobs.length;

  // Filter pills list only the job_status values actually present, in canonical order.
  const statusOpts = useMemo(() => {
    const present = new Set(jobs.map(j => j.job_status).filter(Boolean) as string[]);
    return ['All', ...STATUS_ORDER.filter(s => present.has(s))];
  }, [jobs]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    const data = windowedJobs.filter(j => {
      if (filter !== 'All' && j.job_status !== filter) return false;
      if (q && !j.client.toLowerCase().includes(q) && !j.id.toLowerCase().includes(q)) return false;
      return true;
    });
    return sortRows(data, sort, cellValue);
  }, [windowedJobs, filter, search, sort]);

  if (error) return <div className="px-6 py-5 text-sm text-red-600">{error}</div>;

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Global header — period (primary) + scoped stats on a recessed band */}
      <PageHeader
        stats={[
          { label: 'Jobs', value: total },
        ]}
        scope={scope}
        onScopeChange={setScope}
        counts={winCounts}
      />

      {/* Filter row — status pills, then search */}
      <div className="flex items-center gap-3 px-6 pt-4 pb-4 border-b border-trim flex-shrink-0 flex-wrap gap-y-2">
        <div className="px-2.5 py-0.5 rounded-full text-xs font-semibold" style={{ background: '#F5F7FA', color: '#595A60' }}>{filtered.length} jobs</div>
        <div className="flex gap-1.5 flex-wrap">
          {statusOpts.map(f => (
            <button key={f} onClick={() => setFilter(f)} className="px-3 py-1 rounded-full text-xs font-medium transition-colors"
              style={pillStyle(filter === f, STATUS_PILL[f])}>{f}</button>
          ))}
        </div>
        <div className="ml-auto">
          <SearchInput value={search} onChange={setSearch} placeholder="Search jobs…" width={220} />
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto px-6 pb-6">
        {loading && <div className="text-sm text-muted py-4">Loading…</div>}
        <table className="text-xs" style={{ borderCollapse: 'separate', borderSpacing: 0, width: '100%' }}>
          <thead>
            <tr>
              {COLUMNS.map(h => (
                <SortableTh key={h} label={h} column={h} sort={sort} onSort={toggle} />
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map(job => (
              <tr key={job.id} className="group hover:bg-wash transition-colors cursor-default">
                <td className="px-2 py-1.5 border-b border-wash font-mono text-xs text-navy font-medium whitespace-nowrap">{job.id}</td>
                <td className="px-2 py-1.5 border-b border-wash text-xs font-medium text-ink max-w-[260px] truncate" title={job.client}>{job.client}</td>
                <td className="px-2 py-1.5 border-b border-wash text-xs text-copy">{job.type}</td>
                <td className="px-2 py-1.5 border-b border-wash whitespace-nowrap"><StatusBadge status={job.job_status} /></td>
                <td className="px-2 py-1.5 border-b border-wash text-xs text-muted whitespace-nowrap">{job.date}</td>
              </tr>
            ))}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={5} className="text-center py-12 text-muted text-sm">No jobs match the current filter.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
